import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { Modal, ModalBodyFooter, ModalProcessing, Button, ErrorMessage, Label } from '../../../../components/custom-essentials';
import { DatePicker, TimePicker, SelectSingle, FormikControl, Check } from '../../../../components/form';
import { formatDateApi, formatDate, getTimeObject } from '../../../../components/functions';

const dayFields = [
    { name: 'sundays', label: 'Sun' },
    { name: 'mondays', label: 'Mon' },
    { name: 'tuesdays', label: 'Tue' },
    { name: 'wednesdays', label: 'Wed' },
    { name: 'thursdays', label: 'Thu' },
    { name: 'fridays', label: 'Fri' },
    { name: 'saturdays', label: 'Sat' }
];

const MAX_MEMBERS = 6;
const validationSchema = Yup.object({
    name: Yup.string().trim().required('Group name is required'),
    center: Yup.object().nullable().required('Center is required'),
    startDate: Yup.string().required('Start date is required'),
    endDate: Yup.string()
        .required('End date is required')
        .test('after-start', 'End date cannot be before start date', function (value) {
            const { startDate } = this.parent;
            if (!value || !startDate) return true;
            return new Date(value) >= new Date(startDate);
        }),
    time: Yup.object().nullable().required('Meeting time is required'),
    members: Yup.array()
        .min(1, 'Select at least one student')
        .max(MAX_MEMBERS, 'Groups can only have up to six members')
        .required('Select members')
});

const generateRowId = () => `member-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

function CycleGroupModal(props) {
    const {
        show = true,
        mode = 'create',
        group,
        centers = [],
        students = [],
        groupMembers = [],
        contracts = [],
        onClose,
        createCycleGroup,
        updateCycleGroup,
        deleteCycleGroup
    } = props;

    // Detect portal
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const isReadingPlex = hostname.includes('readingplex');

    const [submitted, setSubmitted] = useState(false);
    const [submissionStatus, setSubmissionStatus] = useState(null);
    const [deleteProcessing, setDeleteProcessing] = useState(false);
    const [deleteStatus, setDeleteStatus] = useState(null);
    const [memberRows, setMemberRows] = useState([]);

    const centerOptions = useMemo(() => centers.map(c => ({
        value: parseInt(c.id),
        label: c.name
    })), [centers]);

    const studentOptions = useMemo(() => students.map(s => ({
        value: s.user_id,
        label: `${s.first_name} ${s.last_name}`,
        object: s
    })), [students]);

    const memberOptions = studentOptions;

    const selectedMembers = useMemo(() => {
        if (!group) return [];
        const memberIds = groupMembers
            .filter(m => parseInt(m.group_id) === parseInt(group.id))
            .map(m => `${m.student}`);
        return memberOptions.filter(opt => memberIds.includes(`${opt.value}`));
    }, [group, groupMembers, memberOptions]);

    // Get contract info for group members
    const memberContracts = useMemo(() => {
        if (!isReadingPlex || !contracts.length || !selectedMembers.length) return {};

        const contractsByMember = {};
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        selectedMembers.forEach(member => {
            const studentId = String(member.value);
            const memberContracts = contracts.filter(c => {
                if (String(c.student) !== studentId) return false;
                // Check if contract is active (end_date >= today or no end_date)
                if (!c.end_date) return true;
                const endDate = new Date(c.end_date);
                return endDate >= today;
            });
            contractsByMember[studentId] = memberContracts;
        });

        return contractsByMember;
    }, [isReadingPlex, contracts, selectedMembers]);

    // Determine if all members have the same contract type
    const groupContractInfo = useMemo(() => {
        if (!isReadingPlex || !selectedMembers.length) return null;

        const memberContractTypes = selectedMembers.map(member => {
            const studentId = String(member.value);
            const memberContracts = contracts.filter(c => {
                if (String(c.student) !== studentId) return false;
                if (!c.end_date) return true;
                const endDate = new Date(c.end_date);
                return endDate >= new Date();
            });
            return memberContracts.map(c => c.type || c.contract_type || 'Unknown');
        });

        // Flatten and get unique types
        const allTypes = [...new Set(memberContractTypes.flat())];

        if (allTypes.length === 0) {
            return { type: 'none', message: 'No active contracts for group members' };
        } else if (allTypes.length === 1) {
            return { type: 'common', contractType: allTypes[0], message: `Common contract: ${allTypes[0]}` };
        } else {
            return { type: 'mixed', contractTypes: allTypes, message: 'Mixed contract types' };
        }
    }, [isReadingPlex, selectedMembers, contracts]);

    const parseBool = (value, fallback = false) => {
        if (value === undefined || value === null) return fallback;
        if (typeof value === 'boolean') return value;
        return parseInt(value) === 1;
    };

    const getCenterOption = (centerId) => {
        if (!centerId) return null;
        return centerOptions.find(opt => parseInt(opt.value) === parseInt(centerId)) || null;
    };

    const defaultTime = group?.time ? getTimeObject(parseInt(group.time)) : getTimeObject(960);

    const initialValues = {
        name: group?.name || '',
        center: getCenterOption(group?.center) || null,
        notes: group?.notes || '',
        // Ensure dates are in 'yyyy-MM-dd' format for date inputs (not ISO timestamps)
        startDate: group?.start_date ? formatDateApi(group.start_date) : formatDateApi(new Date()),
        endDate: group?.end_date ? formatDateApi(group.end_date) : formatDateApi(new Date()),
        time: defaultTime,
        members: mode === 'create' ? [] : selectedMembers,
        sundays: parseBool(group?.sundays, false),
        mondays: parseBool(group?.mondays, true),
        tuesdays: parseBool(group?.tuesdays, true),
        wednesdays: parseBool(group?.wednesdays, true),
        thursdays: parseBool(group?.thursdays, true),
        fridays: parseBool(group?.fridays, false),
        saturdays: parseBool(group?.saturdays, false),
        active: parseBool(group?.active, true)
    };

    const handleSubmit = async (values, formikHelpers) => {
        const { setSubmitting, setStatus } = formikHelpers;
        setStatus(null);

        // Validate and format payload to match database schema
        const centerId = values.center?.value;
        if (!centerId || isNaN(parseInt(centerId))) {
            setStatus('Please select a valid center');
            setSubmitting(false);
            return;
        }

        const timeValue = values.time?.raw;
        if (timeValue === undefined || timeValue === null || isNaN(parseInt(timeValue))) {
            setStatus('Please select a valid meeting time');
            setSubmitting(false);
            return;
        }

        if (!Array.isArray(values.members) || values.members.length === 0) {
            setStatus('Please select at least one group member');
            setSubmitting(false);
            return;
        }

        if (values.members.length > 6) {
            setStatus('Groups can only have up to six members');
            setSubmitting(false);
            return;
        }

        const payload = {
            name: String(values.name || '').trim(),
            center: parseInt(centerId),
            notes: String(values.notes || '').trim(),
            startDate: String(values.startDate || ''),
            endDate: String(values.endDate || ''),
            time: parseInt(timeValue),
            sundays: values.sundays ? 1 : 0,
            mondays: values.mondays ? 1 : 0,
            tuesdays: values.tuesdays ? 1 : 0,
            wednesdays: values.wednesdays ? 1 : 0,
            thursdays: values.thursdays ? 1 : 0,
            fridays: values.fridays ? 1 : 0,
            saturdays: values.saturdays ? 1 : 0,
            active: values.active ? 1 : 0,
            groupMembers: values.members.map(member => {
                const studentId = String(member?.value || '');
                if (!studentId) {
                    throw new Error('Invalid student ID in group members');
                }
                return { student: studentId };
            })
        };

        // Ensure id is integer for edit mode
        let response = null;
        if (mode === 'edit') {
            const groupId = parseInt(group?.id);
            if (!groupId || isNaN(groupId)) {
                setStatus('Invalid group ID');
                setSubmitting(false);
                return;
            }
            payload.id = groupId;
            response = await updateCycleGroup(payload);
        } else {
            response = await createCycleGroup(payload);
        }

        if (!response || response.status >= 400) {
            setStatus(response?.data?.message || 'Unable to save group. Please try again.');
            setSubmitting(false);
            return;
        }

        setSubmitting(false);
        setSubmitted(true);
        setSubmissionStatus(mode === 'edit' ? 'updated' : 'created');
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        validationSchema,
        onSubmit: handleSubmit
    });

    // Debug logging
    useEffect(() => {
        console.log('[CYCLE GROUP MODAL] group prop:', JSON.stringify({ id: group?.id, name: group?.name }, null, 2));
        console.log('[CYCLE GROUP MODAL] formik.values.name:', formik.values.name);
    }, [group, formik.values.name]);

    const modalTitle = mode === 'edit'
        ? `Edit Group${group?.name ? ` - ${group.name}` : ''}`
        : mode === 'delete'
            ? `Delete Group${group?.name ? ` - ${group.name}` : ''}`
            : 'Add Group';

    const closeModal = (shouldRefresh = false) => {
        if (onClose) onClose(shouldRefresh);
    };

    const handleDelete = async () => {
        setDeleteStatus(null);
        setDeleteProcessing(true);
        const response = await deleteCycleGroup({ id: group?.id });
        setDeleteProcessing(false);
        if (!response || response.status >= 400) {
            setDeleteStatus(response?.data?.message || 'Unable to delete group.');
            return;
        }
        setSubmitted(true);
        setSubmissionStatus('deleted');
    };

    const buildMemberRowsFromValues = useCallback((membersList) => {
        if (Array.isArray(membersList) && membersList.length) {
            return membersList.map((member) => ({
                id: generateRowId(),
                value: member
            }));
        }
        return [{ id: generateRowId(), value: null }];
    }, []);

    useEffect(() => {
        setMemberRows(buildMemberRowsFromValues(mode === 'create' ? [] : selectedMembers));
    }, [mode, group?.id, selectedMembers, buildMemberRowsFromValues]);

    const syncMembersToFormik = useCallback((rows) => {
        const sanitized = rows
            .map(row => row.value)
            .filter(Boolean);
        formik.setFieldValue('members', sanitized, true);
    }, [formik]);

    const handleMemberChange = useCallback((index, value) => {
        setMemberRows(prev => {
            const next = prev.map((row, i) => i === index ? { ...row, value } : row);
            syncMembersToFormik(next);
            return next;
        });
    }, [syncMembersToFormik]);

    const handleAddMember = useCallback(() => {
        setMemberRows(prev => {
            if (prev.length >= MAX_MEMBERS) return prev;
            const next = [...prev, { id: generateRowId(), value: null }];
            return next;
        });
    }, []);

    const handleRemoveMember = useCallback((index) => {
        setMemberRows(prev => {
            let next = prev.filter((_, i) => i !== index);
            if (!next.length) {
                next = [{ id: generateRowId(), value: null }];
            }
            syncMembersToFormik(next);
            return next;
        });
    }, [syncMembersToFormik]);

    const renderDaySelectors = (formik) => (
        <div className="flex flex-wrap gap-3">
            {dayFields.map(day => (
                <Check
                    key={`day-check-${day.name}`}
                    color="mpBlue"
                    name={day.name}
                    label={day.label}
                    checked={formik.values[day.name]}
                    onChange={formik.handleChange}
                />
            ))}
        </div>
    );

    const renderSuccess = () => (
        <ModalBodyFooter>
            <Modal.Body>
                <h4 className="mb-2 text-mpBlue">
                    {submissionStatus === 'deleted'
                        ? 'Cycle group deleted successfully.'
                        : `Cycle group ${submissionStatus} successfully.`}
                </h4>
                <div>You can close this dialog to refresh the latest group list.</div>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    color="mpBlue"
                    onClick={() => closeModal(true)}
                >
                    Close
                </Button>
            </Modal.Footer>
        </ModalBodyFooter>
    );

    const renderDelete = () => (
        <ModalBodyFooter>
            <Modal.Body>
                <h4 className="text-mpLRed mb-2">Delete this cycle group?</h4>
                <p>
                    This action will remove the group and all of its memberships.
                    {group?.name ? ` (${group.name})` : ''}
                </p>
                {deleteStatus && (
                    <>
                        <br />
                        <ErrorMessage color="mpLRed">{deleteStatus}</ErrorMessage>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <div className="flex flex-row gap-x-2 flex-wrap">
                    {deleteProcessing && <ModalProcessing />}
                    <Button
                        color="mpLRed"
                        onClick={() => closeModal(false)}
                        disabled={deleteProcessing}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="hol-mpLRed"
                        onClick={handleDelete}
                        disabled={deleteProcessing}
                    >
                        Delete
                    </Button>
                </div>
            </Modal.Footer>
        </ModalBodyFooter>
    );

    const formBody = () => (
                <ModalBodyFooter>
                    <Modal.Body>
                        <div className="grid grid-cols-1 gap-y-4">
                            <div className="grid grid-cols-1 gap-y-2">
                                <FormikControl
                                    id="cycle-group-name"
                                    name="name"
                                    label="Group Name"
                                    placeholder="Enter group name"
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                />
                                {formik.errors.name && (
                                    <ErrorMessage color="mpLRed">
                                        {formik.errors.name}
                                    </ErrorMessage>
                                )}
                            </div>

                            <div className="flex flex-row gap-4 flex-wrap">
                                <div className="grid grid-cols-1 gap-y-2 w-1/2 min-w-[240px]">
                                    <h4>Center</h4>
                                    <SelectSingle
                                        id="cycle-group-center"
                                        name="center"
                                        value={formik.values.center}
                                        options={centerOptions}
                                        onChange={(e) => formik.setFieldValue('center', e.target.value)}
                                    />
                                    {formik.errors.center && (
                                        <ErrorMessage color="mpLRed">
                                            {formik.errors.center}
                                        </ErrorMessage>
                                    )}
                                </div>
                                <div className="grid grid-cols-1 gap-y-2 w-1/2 min-w-[240px]">
                                    <h4>Meeting Time</h4>
                                    <TimePicker
                                        id="cycle-group-time"
                                        name="time"
                                        value={formik.values.time?.formatted24 || ''}
                                        onChange={(e) => formik.setFieldValue('time', e.target.value)}
                                        step={1800}
                                    />
                                    {formik.errors.time && (
                                        <ErrorMessage color="mpLRed">
                                            {formik.errors.time}
                                        </ErrorMessage>
                                    )}
                                </div>
                            </div>

                            <div className="flex flex-row gap-4 flex-wrap">
                                <div className="grid grid-cols-1 gap-y-2 w-1/2 min-w-[240px]">
                                    <h4>Start Date</h4>
                                    <DatePicker
                                        id="cycle-group-start-date"
                                        name="startDate"
                                        value={formik.values.startDate}
                                        onChange={formik.handleChange}
                                    />
                                    {formik.errors.startDate && (
                                        <ErrorMessage color="mpLRed">
                                            {formik.errors.startDate}
                                        </ErrorMessage>
                                    )}
                                </div>
                                <div className="grid grid-cols-1 gap-y-2 w-1/2 min-w-[240px]">
                                    <h4>End Date</h4>
                                    <DatePicker
                                        id="cycle-group-end-date"
                                        name="endDate"
                                        value={formik.values.endDate}
                                        onChange={formik.handleChange}
                                    />
                                    {formik.errors.endDate && (
                                        <ErrorMessage color="mpLRed">
                                            {formik.errors.endDate}
                                        </ErrorMessage>
                                    )}
                                </div>
                            </div>

                            <div>
                                <h4 className="mb-2">Weekdays</h4>
                                {renderDaySelectors(formik)}
                            </div>

                            <div className="grid grid-cols-1 gap-y-2">
                                <h4>Members (max {MAX_MEMBERS})</h4>
                                <div className="flex flex-col gap-y-2">
                                    {memberRows.map((row, index) => (
                                        <div
                                            key={row.id}
                                            className="flex flex-row gap-3 items-center flex-wrap"
                                        >
                                            <div className="flex-1 min-w-[240px]">
                                                <SelectSingle
                                                    id={`cycle-group-member-${row.id}`}
                                                    name={`member-${index}`}
                                                    value={row.value}
                                                    options={memberOptions}
                                                    placeholder="Select student..."
                                            onChange={(e) => handleMemberChange(index, e.target.value)}
                                                />
                                            </div>
                                            <Button
                                                size="sm"
                                                color="mpLRed"
                                        onClick={() => handleRemoveMember(index)}
                                                disabled={memberRows.length === 1 && !row.value}
                                            >
                                                Remove
                                            </Button>
                                        </div>
                                    ))}
                                    <div className="flex flex-row gap-3 items-center">
                                        <Button
                                            size="sm"
                                            color="lte-mpLBlue"
                                    onClick={handleAddMember}
                                            disabled={memberRows.length >= MAX_MEMBERS}
                                        >
                                            + Add Member
                                        </Button>
                                        <div className="text-xs text-gray-500">
                                            You can add up to {MAX_MEMBERS} students per group.
                                        </div>
                                    </div>
                                </div>
                                {formik.errors.members && (
                                    <ErrorMessage color="mpLRed">
                                        {formik.errors.members}
                                    </ErrorMessage>
                                )}
                            </div>

                    {/* Contract Information for ReadingPlex */}
                    {isReadingPlex && mode === 'edit' && selectedMembers.length > 0 && (
                        <div className="grid grid-cols-1 gap-y-2 p-3 bg-gray-50 rounded-lg border">
                            <h4>Contract Information</h4>
                            {groupContractInfo?.type === 'common' && (
                                <div className="flex items-center gap-2">
                                    <Label color="mpGreen">Common Contract</Label>
                                    <span className="font-semibold">{groupContractInfo.contractType}</span>
                                </div>
                            )}
                            {groupContractInfo?.type === 'mixed' && (
                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <Label color="mpOrange">Mixed Contracts</Label>
                                        <span className="text-sm text-gray-600">Members have different contract types</span>
                                    </div>
                                    <div className="text-sm space-y-1">
                                        {selectedMembers.map(member => {
                                            const studentId = String(member.value);
                                            const memberContractList = memberContracts[studentId] || [];
                                            return (
                                                <div key={studentId} className="flex items-center gap-2 pl-2 border-l-2 border-gray-300">
                                                    <span className="font-medium">{member.label}:</span>
                                                    {memberContractList.length > 0 ? (
                                                        memberContractList.map((c, idx) => (
                                                            <span key={c.id || idx} className="text-xs bg-white px-2 py-1 rounded border">
                                                                {c.type || c.contract_type || 'Contract'}
                                                                ({formatDate(c.start_date)} - {formatDate(c.end_date) || 'No end'})
                                                            </span>
                                                        ))
                                                    ) : (
                                                        <span className="text-xs text-mpLRed">No active contract</span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            {groupContractInfo?.type === 'none' && (
                                <div className="flex items-center gap-2">
                                    <Label color="mpLRed">No Contracts</Label>
                                    <span className="text-sm text-gray-600">No active contracts found for group members</span>
                                </div>
                            )}
                        </div>
                    )}

                            <div className="grid grid-cols-1 gap-y-2">
                                <FormikControl
                                    id="cycle-group-notes"
                                    name="notes"
                                    as="textarea"
                                    rows="4"
                                    label="Notes"
                                    placeholder="Internal notes..."
                                    value={formik.values.notes}
                                    onChange={formik.handleChange}
                                />
                            </div>

                            <div>
                                <Check
                                    color="mpBlue"
                                    name="active"
                                    label="Group is active"
                                    checked={formik.values.active}
                                    onChange={formik.handleChange}
                                />
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <div className="flex flex-row gap-x-2 flex-wrap items-center">
                            {formik.isSubmitting && <ModalProcessing />}
                            {formik.status && (
                                <ErrorMessage color="mpLRed">
                                    {formik.status}
                                </ErrorMessage>
                            )}
                            <Button
                                color="mpLRed"
                                onClick={() => closeModal(false)}
                                disabled={formik.isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                color="mpBlue"
                                onClick={formik.handleSubmit}
                                disabled={formik.isSubmitting}
                            >
                                {mode === 'edit' ? 'Save Changes' : 'Create Group'}
                            </Button>
                        </div>
                    </Modal.Footer>
                </ModalBodyFooter>
    );

    // Determine which content to show - must be after all render functions are defined
    const modalContent = submitted
        ? renderSuccess()
        : mode === 'delete'
            ? renderDelete()
            : formBody();

    return (
        <Modal
            show={show}
            onHide={() => closeModal(false)}
            preventClickout={false}
            className="w-2/3"
        >
            <Modal.Header>
                <h2>{modalTitle}</h2>
            </Modal.Header>
            {modalContent}
        </Modal>
    );
}

export default CycleGroupModal;
